// "Assign to…" for the stores selected on the map: the console's form of the iOS bulk assign (BulkActionsSheet "Assign
// owner", TerritoryDetailView "Bulk assign"). Pick a person (or Unassigned), see how many stores change owner and who
// owns them now, then save. The edits are `assignOwnerEdits` (stores that already have that owner are skipped; each
// changed store also gets the iOS "Assigned" activity), saved 500 stores at a time with a progress bar so a
// 5,000-store assign never blocks the page. Each owner change reaches the new owner's iPhone with the next sync, where
// the store shows up in their lists and on their map.

import { UserRoundCheck } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import type { RecordPayload, UserPayload } from '@/contracts/models';
import { useViewer } from '@/data/hooks';
import {
  ASSIGN_CHUNK_SIZE,
  UNASSIGNED_OWNER_VALUE,
  assignOwnerEdits,
  chunked,
  ownerBreakdown,
  ownerChanges,
  ownerPickerOptions,
  withOwnerOn,
} from '@/domain/assign';
import { formatGrouped } from '@/domain/format';
import { fullName, idKey } from '@/domain/util';
import { useSave } from '@/sync/hooks';
import { Button, Dialog, Field, KeyValueRow, ProgressBar, Select } from '@/ui';
import { ChunkedSaveError, saveInChunks, storeCountText } from './mapSelection';

/** The Owner hint once Unassigned is chosen: unowned stores are on every rep's phone (iOS and server visibility). */
export const UNASSIGNED_HINT = 'Unassigned stores are visible to every rep in the Ground Team app.';
/** The Owner hint otherwise. */
export const OWNER_PICKER_HINT = 'Reps, managers and admins you can see on the team.';

/** Rows of "Currently" before the rest are summed into "Others". */
const MAX_OWNER_ROWS = 5;

export interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The selected stores on the map (decoded payloads only, so no edit is built from a store a newer app wrote). Read
   * when the dialog opens: the saves themselves change what the map shows (an "Unassigned" filter drops every store
   * as it is assigned), and the numbers in the dialog must not count down under the person while it saves.
   */
  stores: readonly RecordPayload[];
  /** `pickerUsers`: the active people the viewer can see (reps, managers and admins), by name. */
  people: readonly UserPayload[];
  /** Every visible person by lower-case id, to name the current owners (and the old and new owner in the history). */
  users: ReadonlyMap<string, UserPayload>;
  /** After the save: how many stores changed, and the new owner's name (null for Unassigned). */
  onAssigned: (count: number, ownerName: string | null) => void;
}

export function AssignDialog(props: AssignDialogProps) {
  // Mounted only while open, so every opening starts with no owner chosen.
  if (!props.open) return null;
  return <OpenAssignDialog {...props} />;
}

function OpenAssignDialog({ onClose, stores: storesAtOpen, people, users, onAssigned }: AssignDialogProps) {
  const [stores, setStores] = useState(storesAtOpen);
  const save = useSave();
  const viewer = useViewer();
  const formId = useId();
  const [choice, setChoice] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  // `progress` reaches the button one render late; a double Enter must not start two saves.
  const inFlight = useRef(false);

  const chosen = choice !== '';
  const ownerId = choice === '' || choice === UNASSIGNED_OWNER_VALUE ? null : choice;
  const owner = ownerId ? people.find((u) => u.id === ownerId) : undefined;
  const ownerName = owner ? fullName(owner) || 'Unnamed' : null;

  const changing = useMemo(() => (chosen ? ownerChanges(stores, ownerId).length : 0), [stores, ownerId, chosen]);
  const current = useMemo(() => ownerBreakdown(stores, users), [stores, users]);
  const shown = current.slice(0, MAX_OWNER_ROWS);
  const others = current.slice(MAX_OWNER_ROWS).reduce((n, s) => n + s.count, 0);
  const saving = progress !== null;
  const ownerOptions = useMemo(() => ownerPickerOptions(people, viewer?.profileId, { unassigned: true }), [people, viewer?.profileId]);

  const assign = async () => {
    if (!chosen || changing === 0 || inFlight.current) return;
    inFlight.current = true;
    setErrorText(null);
    // One group per store (its edit and its "Assigned" activity), so batches split between stores, never inside one.
    // Counts are stores: the replica skips only the record edits (a store deleted elsewhere), one per store.
    const groups = assignOwnerEdits(stores, ownerId, { by: viewer?.profileId, at: new Date(), users });
    const savedIds = new Set<string>();
    try {
      const result = await saveInChunks(
        chunked(groups, ASSIGN_CHUNK_SIZE),
        (chunk) => {
          const out = save(chunk.flat());
          for (const group of chunk) savedIds.add(idKey(group[0]!.id));
          return out;
        },
        { onProgress: (done, total) => setProgress({ done, total }) },
      );
      onAssigned(result.applied, ownerName);
    } catch (error) {
      // The batches before the refused one are saved: count those stores as the new owner's, so "Try again" only
      // sends the rest (and never logs a second "Assigned" for a store that already changed).
      if (savedIds.size > 0) setStores((list) => withOwnerOn(list, savedIds, ownerId));
      const saved = error instanceof ChunkedSaveError ? error.applied : 0;
      const reason = (error instanceof Error && error.message ? error.message : 'Something went wrong').replace(/\.$/, '');
      setErrorText(
        saved > 0
          ? `${storeCountText(saved)} ${saved === 1 ? 'was' : 'were'} assigned, then the rest could not be saved (${reason}). Try again to finish.`
          : `The stores could not be assigned (${reason}). Try again.`,
      );
      setProgress(null);
    } finally {
      inFlight.current = false;
    }
  };

  const unchanged = stores.length - changing;
  return (
    <Dialog
      open
      onClose={onClose}
      // Closing mid-save would hide how far it got; the save itself carries on either way.
      dismissible={!saving}
      title={`Assign ${storeCountText(stores.length)}`}
      description="The new owner sees these stores in the Ground Team app after their next sync."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            icon={<UserRoundCheck />}
            loading={saving}
            disabled={!chosen || changing === 0}
          >
            {saving ? 'Assigning…' : chosen && changing > 0 ? `Assign ${storeCountText(changing)}` : 'Assign'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void assign();
        }}
      >
        <fieldset disabled={saving} className="flex flex-col gap-1">
          <Field
            label="Owner"
            hint={ownerId === null && chosen ? UNASSIGNED_HINT : OWNER_PICKER_HINT}
          >
            <Select
              value={choice}
              onChange={(e) => {
                setChoice(e.currentTarget.value);
                setErrorText(null);
              }}
              placeholder="Choose an owner…"
              options={ownerOptions}
            />
          </Field>
        </fieldset>

        <section aria-label="What changes" className="rounded-control border border-line px-3 py-1">
          <dl className="divide-y divide-line">
            <KeyValueRow label="Selected stores" value={formatGrouped(stores.length)} />
            <KeyValueRow label="Owner will change" value={chosen ? formatGrouped(changing) : '—'} />
            {chosen && unchanged > 0 && (
              <KeyValueRow label={ownerId === null ? 'Already unassigned' : 'Already theirs'} value={formatGrouped(unchanged)} />
            )}
          </dl>
        </section>

        {current.length > 0 && (
          <section aria-label="Owned now by" className="flex flex-col gap-1">
            <h3 className="text-[11px] font-bold tracking-[0.06em] text-fg-muted uppercase">Owned now by</h3>
            <dl>
              {shown.map((s) => (
                <KeyValueRow key={s.ownerId ?? s.name} label={s.name} value={formatGrouped(s.count)} className="py-1" />
              ))}
              {others > 0 && <KeyValueRow label="Others" value={formatGrouped(others)} className="py-1" />}
            </dl>
          </section>
        )}

        {progress && (
          <ProgressBar
            value={progress.total === 0 ? 1 : progress.done / progress.total}
            label="Assigning stores"
            showLabel
            detail={`${formatGrouped(progress.done)} of ${formatGrouped(progress.total)}`}
          />
        )}
        {chosen && changing === 0 && !saving && (
          <p className="text-sm text-fg-muted">
            {ownerId === null ? 'None of these stores has an owner.' : `${ownerName ?? 'This person'} already owns all of these stores.`}
          </p>
        )}
        {errorText && (
          <p role="alert" className="text-sm text-danger">
            {errorText}
          </p>
        )}
      </form>
    </Dialog>
  );
}
